import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, UserX, PlaneLanding, FileWarning, ShieldAlert, Wallet, CheckCheck, RefreshCw,
} from 'lucide-react';
import {
  useNotifications, useMarkRead, useRunDailyChecks, type NotificationRow,
} from './useNotifications';
import { useSession } from '@/core/session';
import { useDismiss } from '@/core/useDismiss';
import { cn } from '@/components/ui';
import { type Vars, type TranslationKey, useT } from '@/core/i18n';
import { errorMessage } from '@/core/errorMessage';

const CATEGORY_ICON: Record<string, typeof Bell> = {
  absence: UserX,
  leave_overdue: PlaneLanding,
  document_expiry: FileWarning,
  integrity: ShieldAlert,
  payroll: Wallet,
  approval: CheckCheck,
};

const SEVERITY_STYLE: Record<NotificationRow['severity'], string> = {
  critical: 'bg-danger-subtle text-danger',
  warning: 'bg-warn-subtle text-warn',
  info: 'bg-info-subtle text-info',
};

export function NotificationBell() {
  const t = useT();
  const { data, isLoading } = useNotifications();
  const markRead = useMarkRead();
  const runChecks = useRunDailyChecks();
  const { can } = useSession();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [ran, setRan] = useState<string | null>(null);

  const items = data ?? [];
  const unread = items.filter((n) => !n.read_at);

  async function runNow() {
    setRan(null);
    try {
      const result = await runChecks.mutateAsync(undefined);
      setRan(
        t('nb.sweepResult', {
          absence: result.absenceAlerts,
          leave: result.overdueLeaveAlerts,
          documents: result.documentAlerts,
          queued: result.queuedForRecompute,
        }),
      );
    } catch (caught) {
      setRan(errorMessage(caught));
    }
  }

  // Wraps the trigger as well as the panel — see useDismiss for why.
  const ref = useDismiss<HTMLDivElement>(open, () => setOpen(false));

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={
          unread.length > 0
            ? t('nb.unreadLabel', { count: unread.length })
            : 'Notifications'
        }
        aria-expanded={open}
        className="relative p-2 rounded-card text-muted hover:bg-surface-2 hover:text-text transition-colors"
      >
        <Bell size={18} />
        {unread.length > 0 && (
          <span
            className={cn(
              'absolute -top-0.5 -right-0.5 min-w-[17px] h-[17px] px-1',
              'rounded-pill grid place-items-center text-[9.5px] font-bold',
              unread.some((n) => n.severity === 'critical')
                ? 'bg-danger text-danger-fg'
                : 'bg-warn text-warn-fg',
            )}
          >
            {unread.length > 99 ? '99+' : unread.length}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Full width on a phone, anchored panel on a desktop. */}
          <div
            className={cn(
              'absolute z-20 mt-1.5 bg-surface border border-line rounded-card shadow-pop',
              'end-0 w-[min(380px,calc(100vw-2rem))] max-h-[70dvh] flex flex-col',
            )}
          >
            <header className="flex items-center justify-between gap-2 px-4 h-12 border-b border-line shrink-0">
              <h2 className="text-[13.5px] font-bold">
                Alerts
                {unread.length > 0 && (
                  <span className="text-muted font-normal"> · {unread.length} new</span>
                )}
              </h2>
              <div className="flex items-center gap-1">
                {can('attendance.view') && (
                  <button
                    type="button"
                    onClick={() => void runNow()}
                    disabled={runChecks.isPending}
                    title={t('nb.sweepNow')}
                    className="p-1.5 rounded-card text-subtle hover:text-text hover:bg-surface-2 transition-colors"
                  >
                    <RefreshCw
                      size={14}
                      className={runChecks.isPending ? 'animate-spin' : ''}
                    />
                  </button>
                )}
                {unread.length > 0 && (
                  <button
                    type="button"
                    onClick={() => markRead.mutate(unread.map((n) => n.id))}
                    className="text-[11.5px] text-brand font-medium px-2 py-1 rounded-card hover:bg-brand-subtle transition-colors"
                  >
                    {t('nb.markAllRead')}
                  </button>
                )}
              </div>
            </header>

            {ran && (
              <p className="px-4 py-2 text-[11.5px] text-muted bg-surface-2 border-b border-line shrink-0">
                {ran}
              </p>
            )}

            <div className="overflow-y-auto flex-1">
              {isLoading ? (
                <p className="px-4 py-8 text-center text-[12.5px] text-subtle">Loading…</p>
              ) : items.length === 0 ? (
                <div className="px-6 py-10 text-center">
                  <Bell size={22} className="mx-auto text-subtle mb-2" />
                  <p className="text-[13px] font-semibold">{t('nb.nothingNeedsYou')}</p>
                  <p className="text-[11.5px] text-muted mt-1 leading-snug">
                    {t('nb.whatAppears')}
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-line">
                  {items.map((item) => {
                    const Icon = CATEGORY_ICON[item.category] ?? Bell;
                    return (
                      <li key={item.id}>
                        <button
                          type="button"
                          onClick={() => {
                            if (!item.read_at) markRead.mutate([item.id]);
                            if (item.action_url) navigate(item.action_url);
                            setOpen(false);
                          }}
                          className={cn(
                            'w-full text-start px-4 py-3 flex gap-3 transition-colors',
                            'hover:bg-surface-2',
                            !item.read_at && 'bg-brand-subtle/40',
                          )}
                        >
                          <span
                            className={cn(
                              'shrink-0 w-8 h-8 rounded-card grid place-items-center',
                              SEVERITY_STYLE[item.severity],
                            )}
                          >
                            <Icon size={15} />
                          </span>

                          <span className="min-w-0 flex-1">
                            <span className="flex items-start justify-between gap-2">
                              <span className="text-[12.5px] font-semibold leading-snug">
                                {item.title}
                              </span>
                              {/* A count, not a repeated row — one man absent
                                  a month is one thread, not thirty alerts. */}
                              {item.occurrence > 1 && (
                                <span className="shrink-0 text-[10px] font-bold text-subtle tabular">
                                  ×{item.occurrence}
                                </span>
                              )}
                            </span>
                            {item.body && (
                              <span className="block text-[11.5px] text-muted mt-0.5 leading-snug">
                                {item.body}
                              </span>
                            )}
                            <span className="block text-[10.5px] text-subtle mt-1">
                              {relative(item.created_at, t)}
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function relative(
  iso: string,
  t: (key: TranslationKey, vars?: Vars) => string,
): string {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return t('nb.justNow');
  if (minutes < 60) return t('nb.minsAgo', { count: minutes });
  if (minutes < 60 * 24) return t('nb.hoursAgo', { count: Math.round(minutes / 60) });

  const days = Math.round(minutes / 1440);
  return days === 1 ? t('nb.yesterday') : t('nb.daysAgoShort', { count: days });
}
